/**
 * Mixpanel Analytics - Web Frontend
 *
 * Simple Mixpanel integration for the web client
 */

import mixpanel from 'mixpanel-browser';
import { createLogger } from '@ctrl-enter/tools/utils';
import type { User } from '@ctrl-enter/types';

const logger = createLogger('web:service:analytics');

let isInitialized = false;

/**
 * Initialize Mixpanel
 * Call this once in your app initialization (e.g., main.tsx)
 */
export function initMixpanel() {
  const token = import.meta.env.VITE_MIXPANEL_TOKEN;
  const enabled = import.meta.env.VITE_MIXPANEL_ENABLED === 'true';

  if (!enabled || !token) {
    logger.info('[Mixpanel] Analytics disabled');
    isInitialized = false;
    return;
  }

  try {
    mixpanel.init(token, {
      debug: import.meta.env.DEV,
      track_pageview: true,
      persistence: 'localStorage',
      ignore_dnt: false,
    });
    isInitialized = true;
    logger.info('[Mixpanel] Analytics initialized');
  } catch (error) {
    logger.warn('[Mixpanel] Failed to initialize:', error);
    isInitialized = false;
  }
}

/**
 * Identify user after login
 * Call this from your AuthProvider after successful authentication
 */
export function identifyUser(user: User) {
  if (!isInitialized) return;

  try {
    mixpanel.identify(user.id);
    mixpanel.people.set({
      $email: user.email,
      $name: user.name,
      organizationId: user.organizationId,
    });
    logger.info('[Mixpanel] Identified user:', user.id);
  } catch (error) {
    logger.warn('[Mixpanel] Failed to identify user:', error);
  }
}

/**
 * Reset identity on logout
 */
export function resetIdentity() {
  if (!isInitialized) return;

  try {
    mixpanel.reset();
    logger.info('[Mixpanel] Reset user identity');
  } catch (error) {
    logger.warn('[Mixpanel] Failed to reset identity:', error);
  }
}

/**
 * Create a safe wrapper around mixpanel that checks initialization
 */
const safeMixpanel = {
  track: (eventName: string, properties?: Record<string, unknown>) => {
    if (!isInitialized) {
      logger.debug('[Mixpanel] Skipping track (not initialized):', eventName);
      return;
    }
    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      logger.warn('[Mixpanel] Track failed:', eventName, error);
    }
  },
  identify: (userId: string) => {
    if (!isInitialized) return;
    try {
      mixpanel.identify(userId);
    } catch (error) {
      logger.warn('[Mixpanel] Identify failed:', error);
    }
  },
  people: {
    set: (properties: Record<string, unknown>) => {
      if (!isInitialized) return;
      try {
        mixpanel.people.set(properties);
      } catch (error) {
        logger.warn('[Mixpanel] People.set failed:', error);
      }
    },
  },
  reset: () => {
    if (!isInitialized) return;
    try {
      mixpanel.reset();
    } catch (error) {
      logger.warn('[Mixpanel] Reset failed:', error);
    }
  },
};

// Export safe wrapper instead of raw mixpanel
export { safeMixpanel as mixpanel };
